import { useEffect } from "react";
import { EDITION } from "@/lib/edition";

// Edition-aware SEO: sets the document <title>, meta description, canonical,
// Open Graph + Twitter tags and (optionally) JSON-LD structured data.
// Backwards compatible: useSeo(title, description, image, options?).
//   options = { canonicalPath, jsonLd, type, noindex }
const BRAND = `${EDITION.brand}${EDITION.mark || ""}`;
const DEFAULT_TITLE = EDITION.id === "india"
  ? "IndiaAlumni — Reconnect With Your Past. Build Your Future. Leave Your Legacy."
  : EDITION.id === "edupulse"
    ? "IndianEduPulse — Discover India's Education. Connect with Its People. Preserve Its History."
    : "AlumniPulse — Every Institution. Every Generation. One Global Community.";
const DEFAULT_OG_IMAGE = EDITION.id === "edupulse"
  ? `${EDITION.url}/indianedupulse-logo.png`
  : `${EDITION.url}${EDITION.id === "india" ? "/indiaalumni-hero.jpg" : "/storyboard1.png"}`;

// Build a schema.org BreadcrumbList node from [label, path] pairs.
// Pass an array of these (plus entity nodes) as options.jsonLd — it is
// serialized as-is, so an array becomes multiple JSON-LD graph nodes.
export function breadcrumbLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: (items || []).map(([name, path], i) => ({
      "@type": "ListItem",
      position: i + 1,
      name,
      item: `${EDITION.url}${path}`,
    })),
  };
}

function upsertMeta(attr, key, val) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (val == null) { if (el) el.remove(); return; }
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", val);
}

function upsertLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!href) { if (el) el.remove(); return; }
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
}

export function useSeo(title, description, image, options = {}) {
  const { canonicalPath, jsonLd, type = "website", noindex = false } = options;
  const jsonLdStr = jsonLd ? JSON.stringify(jsonLd) : null;
  useEffect(() => {
    const fullTitle = title ? `${title} · ${BRAND}` : DEFAULT_TITLE;
    document.title = fullTitle;

    const path = canonicalPath || (typeof window !== "undefined" ? window.location.pathname : "/");
    const canonical = `${EDITION.url}${path}`;
    const ogImage = image || DEFAULT_OG_IMAGE;

    upsertMeta("name", "description", description);
    upsertLink("canonical", canonical);
    upsertMeta("name", "robots", noindex ? "noindex,nofollow" : "index,follow");

    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:site_name", EDITION.brand);
    upsertMeta("property", "og:image", ogImage);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", ogImage);

    let ld = document.querySelector('script[data-seo-jsonld="1"]');
    if (jsonLdStr) {
      if (!ld) { ld = document.createElement("script"); ld.type = "application/ld+json"; ld.setAttribute("data-seo-jsonld", "1"); document.head.appendChild(ld); }
      ld.textContent = jsonLdStr;
    } else if (ld) { ld.remove(); }

    return () => {
      const cur = document.querySelector('script[data-seo-jsonld="1"]');
      if (cur) cur.remove();
    };
  }, [title, description, image, canonicalPath, jsonLdStr, type, noindex]);
}
